import React, { useEffect, useRef, useState } from 'react';

const TOOLTIP_SELECTOR = '[data-tooltip], [title], [aria-label]';

const getTooltipText = (element) => {
  if (!element) return '';
  return (
    element.getAttribute('data-tooltip') ||
    element.getAttribute('title') ||
    element.getAttribute('data-native-title') ||
    element.getAttribute('aria-label') ||
    ''
  ).trim();
};

const GlobalTooltip = () => {
  const [tooltip, setTooltip] = useState(null);
  const activeElementRef = useRef(null);

  useEffect(() => {
    const hide = () => {
      const active = activeElementRef.current;
      if (active?.dataset?.nativeTitle) {
        active.setAttribute('title', active.dataset.nativeTitle);
        delete active.dataset.nativeTitle;
      }
      activeElementRef.current = null;
      setTooltip(null);
    };

    const showFor = (target) => {
      const element = target?.closest?.(TOOLTIP_SELECTOR);
      if (!element) return;

      const text = getTooltipText(element);
      if (!text) return;

      if (activeElementRef.current && activeElementRef.current !== element) hide();
      activeElementRef.current = element;

      const nativeTitle = element.getAttribute('title');
      if (nativeTitle) {
        element.dataset.nativeTitle = nativeTitle;
        element.removeAttribute('title');
      }

      const rect = element.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const estimatedWidth = Math.min(320, Math.max(120, text.length * 7));
      const left = Math.min(
        viewportWidth - estimatedWidth / 2 - 12,
        Math.max(estimatedWidth / 2 + 12, rect.left + rect.width / 2)
      );
      const placeBelow = rect.top < 70;

      setTooltip({
        text,
        left,
        top: placeBelow ? rect.bottom + 10 : rect.top - 10,
        placement: placeBelow ? 'below' : 'above'
      });
    };

    const handlePointerOver = (event) => showFor(event.target);
    const handlePointerOut = (event) => {
      const active = activeElementRef.current;
      if (!active) return;
      if (event.relatedTarget && active.contains(event.relatedTarget)) return;
      if (event.target === active || active.contains(event.target)) hide();
    };
    const handleFocusIn = (event) => showFor(event.target);
    const handleFocusOut = (event) => {
      const active = activeElementRef.current;
      if (!active) return;
      if (event.relatedTarget && active.contains(event.relatedTarget)) return;
      hide();
    };
    const handleViewportChange = () => hide();

    document.addEventListener('pointerover', handlePointerOver, true);
    document.addEventListener('pointerout', handlePointerOut, true);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);
    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);

    return () => {
      document.removeEventListener('pointerover', handlePointerOver, true);
      document.removeEventListener('pointerout', handlePointerOut, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusOut, true);
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
      hide();
    };
  }, []);

  if (!tooltip) return null;

  const transform = tooltip.placement === 'below'
    ? 'translate(-50%, 0)'
    : 'translate(-50%, -100%)';

  return (
    <div
      className={`theme-global-tooltip theme-global-tooltip-${tooltip.placement}`}
      style={{ left: tooltip.left, top: tooltip.top, transform }}
      role="tooltip"
    >
      {tooltip.text}
    </div>
  );
};

export default GlobalTooltip;